import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import {
  ArrowRightIcon,
  CalendarIcon,
  MessagesSquareIcon,
  RssIcon,
} from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { groupBannerImage, groupCoverImage } from "simplydo/core/images";
import { initUseQuery } from "simplydo/core/query";
import { useTheme } from "simplydo/react/theme";
import { ButtonLink } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { H3, H4, Subheader } from "simplydo/react/ui/header";
import { Icon, type IconData } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import {
  compareGroupsByRecentActivity,
  getStoredGroupVisits,
} from "utils/groupVisits";

import HomeCarousel from "../HomeCarousel";
import { HomeLink } from "../HomeUI";
import HomeBlock from "./HomeBlock";

const recentActivityScore = (group: {
  recentCommentCount?: number;
  recentActivityFeedCount?: number;
}) => (group.recentCommentCount ?? 0) + (group.recentActivityFeedCount ?? 0);

const GroupsBlock = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const user = useCurrentUser();
  const groupsQuery = useQuery(initUseQuery(api.users.dashboard, []));

  if (!groupsQuery.data?.groups?.length) return null;

  const dashboardGroups = groupsQuery.data.groups;
  const groupPriority = (group: (typeof dashboardGroups)[number]) => {
    if (
      (group.unseenCommentCount ?? 0) > 0 ||
      (group.unseenActivityFeedCount ?? 0) > 0
    ) {
      return 0;
    }
    if (
      (group.todaysCalendarEvents?.length ?? 0) > 0 ||
      (group.upcomingCalendarEventCount ?? 0) > 0
    ) {
      return 1;
    }
    return 2;
  };
  const visits = getStoredGroupVisits(user?._id, user?.ownerOrganisation?._id);
  const groups = [...dashboardGroups].sort((a, b) => {
    const priorityDiff = groupPriority(a) - groupPriority(b);
    if (priorityDiff) return priorityDiff;

    const recentDiff = recentActivityScore(b) - recentActivityScore(a);
    if (recentDiff) return recentDiff;

    if (groupPriority(a) === 2) {
      return compareGroupsByRecentActivity(visits, a, b);
    }
    return 0;
  });

  return (
    <HomeBlock>
      <CardContent>
        <H3>
          {t("users.home.groups.engage")}
          <Subheader>{t("users.home.groups.info")}</Subheader>
        </H3>

        <HomeCarousel>
          {groups.map((group) => {
            const unseenMessages = group.unseenCommentCount ?? 0;
            const unseenPosts = group.unseenActivityFeedCount ?? 0;
            const recentMessages = group.recentCommentCount ?? 0;
            const recentPosts = group.recentActivityFeedCount ?? 0;
            const todaysEvents = group.todaysCalendarEvents ?? [];
            const upcomingEvents = group.upcomingCalendarEventCount ?? 0;
            const discussionId =
              group.latestUnseenDiscussion || group.latestRecentDiscussion;
            const discussionPath = discussionId
              ? `/groups/${group._id}/discussions/${discussionId}`
              : `/groups/${group._id}`;
            const activityParts: {
              key: string;
              to: string;
              label: string;
              icon?: IconData;
            }[] = [];
            if (unseenPosts > 0) {
              activityParts.push({
                key: "posts",
                to: `/groups/${group._id}/feed`,
                label: t("users.home.groups.newPosts", { count: unseenPosts }),
                icon: RssIcon,
              });
            }
            if (recentPosts > 0) {
              activityParts.push({
                key: "recent-posts",
                to: `/groups/${group._id}/feed`,
                label: t("users.home.groups.recentPosts", {
                  count: recentPosts,
                }),
                icon: RssIcon,
              });
            }
            if (unseenMessages > 0) {
              activityParts.push({
                key: "messages",
                to: discussionPath,
                label: t("users.home.groups.newMessages", {
                  count: unseenMessages,
                }),
                icon: MessagesSquareIcon,
              });
            }
            if (recentMessages > 0) {
              activityParts.push({
                key: "recent-messages",
                to: discussionPath,
                label: t("users.home.groups.recentMessages", {
                  count: recentMessages,
                }),
                icon: MessagesSquareIcon,
              });
            }
            for (const event of todaysEvents) {
              const timeLabel =
                event.allDay || !event.from
                  ? event.title
                  : `${formatDate(event.from, "HH:mm")} · ${event.title}`;
              activityParts.push({
                key: `event-${event.calendar}-${event.id}`,
                to: `/groups/${group._id}/calendars/${event.calendar}/events/${event.id}`,
                label: timeLabel,
                icon: CalendarIcon,
              });
            }
            if (upcomingEvents > 0) {
              activityParts.push({
                key: "upcoming-events",
                to: `/groups/${group._id}`,
                label: t("users.home.groups.upcomingEvents", {
                  count: upcomingEvents,
                }),
                icon: CalendarIcon,
              });
            }
            const imageSrc = group.bannerImage
              ? groupBannerImage(group, "640x100")
              : groupCoverImage(group);

            return (
              <Card
                key={group._id}
                style={{ margin: 0, flex: 1 }}
                elevation="small"
              >
                <Image
                  src={imageSrc}
                  height={100}
                  style={{
                    objectFit: "cover",
                    backgroundColor: theme.colors.primary.soft,
                  }}
                />
                <CardContent>
                  <H4>
                    {group.name}
                    {activityParts.length > 0 ? (
                      <Subheader
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                        }}
                      >
                        {activityParts.map((part) => (
                          <HomeLink
                            key={part.key}
                            to={part.to}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            {part.icon ? (
                              <Icon icon={part.icon} size="small" />
                            ) : null}
                            {part.label}
                          </HomeLink>
                        ))}
                      </Subheader>
                    ) : null}
                  </H4>
                </CardContent>
                <CardContent
                  style={{
                    display: "flex",
                    flex: 0,
                    justifyContent: "flex-end",
                  }}
                >
                  <ButtonLink
                    color="primary"
                    variant="outline"
                    style={{ fontWeight: "bold" }}
                    size="small"
                    to={`/groups/${group._id}`}
                  >
                    {t("generic.viewType", { entity: t("generic.group") })}
                  </ButtonLink>
                </CardContent>
              </Card>
            );
          })}
        </HomeCarousel>
      </CardContent>
      <CardContent color="primary" textAlign="right" style={{ flexGrow: 0 }}>
        <ButtonLink color="primary" variant="ghost" size="small" to="/groups">
          {t("users.home.groups.view.all")}
          <Icon icon={ArrowRightIcon} />
        </ButtonLink>
      </CardContent>
    </HomeBlock>
  );
};

export default GroupsBlock;
